import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '@/api/client';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { SelectField, TextAreaField, TextField } from '@/components/ui/Field';
import { PageHeader } from '@/components/ui/PageHeader';
import { LoadingLine } from '@/components/ui/States';
import { StaffOnly } from '@/features/admin-content/StaffOnly';
import { useCanEditContent, useCurrentUser } from '@/features/admin-content/use-current-user';
import { authClient } from '@/lib/auth-client';

export const Route = createFileRoute('/admin/bank/new')({
  component: BankNewPage,
});

function kindForSkill(skill: 'listening' | 'reading' | 'writing' | 'speaking') {
  if (skill === 'listening') return 'audio' as const;
  if (skill === 'writing' || skill === 'speaking') return 'prompt' as const;
  return 'passage' as const;
}

function BankNewPage() {
  const navigate = useNavigate();
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const meQuery = useCurrentUser();
  const allowed = useCanEditContent();
  const [title, setTitle] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [kind, setKind] = useState<'passage' | 'audio' | 'prompt'>('passage');
  const [skill, setSkill] = useState<'listening' | 'reading' | 'writing' | 'speaking'>('reading');
  const [license, setLicense] = useState<'ownWork' | 'aiGenerated'>('ownWork');
  const [examVariant, setExamVariant] = useState<'ielts-academic' | 'ielts-general-training' | ''>(
    'ielts-academic',
  );
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post('/api/v1/admin/bank/stimuli', {
        body: {
          kind,
          title,
          bodyText,
          license,
          skill,
          examVariant: examVariant === '' ? null : examVariant,
        },
      }),
    onSuccess: (row) => {
      void navigate({ to: '/admin/bank/$stimulusId', params: { stimulusId: row.id } });
    },
    onError: (err) => {
      setError(
        err instanceof ApiError
          ? 'Check the title, text and licence, then try again.'
          : 'We could not save this. Try again.',
      );
    },
  });

  if (sessionPending || meQuery.isPending) return <LoadingLine>Loading</LoadingLine>;
  if (!session) {
    void navigate({ to: '/sign-in' });
    return null;
  }
  if (!allowed) {
    return (
      <StaffOnly
        title="You cannot create bank items"
        description="You can read the bank, but only staff with write access can add material."
      />
    );
  }

  return (
    <div className="grid gap-6">
      <PageHeader
        eyebrow="Workspace"
        title="Create stimulus"
        description="Use original text only. Never paste real exam material."
      />
      {error ? (
        <Alert tone="danger" title="We could not create this">
          {error}
        </Alert>
      ) : null}
      <Card padding="lg" className="grid gap-4">
        <TextField
          label="Title"
          value={title}
          onChange={(event) => {
            setTitle(event.target.value);
          }}
        />
        <SelectField
          label="Kind"
          value={kind}
          onChange={(event) => {
            setKind(event.target.value as typeof kind);
          }}
        >
          <option value="passage">Passage</option>
          <option value="audio">Audio / script</option>
          <option value="prompt">Writing or speaking prompt</option>
        </SelectField>
        <SelectField
          label="Skill"
          value={skill}
          onChange={(event) => {
            const next = event.target.value as typeof skill;
            setSkill(next);
            setKind(kindForSkill(next));
          }}
        >
          <option value="reading">Reading</option>
          <option value="listening">Listening</option>
          <option value="writing">Writing</option>
          <option value="speaking">Speaking</option>
        </SelectField>
        <SelectField
          label="Used in"
          value={examVariant}
          onChange={(event) => {
            setExamVariant(event.target.value as typeof examVariant);
          }}
        >
          <option value="ielts-academic">IELTS Academic</option>
          <option value="ielts-general-training">IELTS General Training</option>
          <option value="">Both</option>
        </SelectField>
        <SelectField
          label="Licence"
          value={license}
          onChange={(event) => {
            setLicense(event.target.value as typeof license);
          }}
        >
          <option value="ownWork">Our own work</option>
          <option value="aiGenerated">Made with AI</option>
        </SelectField>
        <TextAreaField
          label="Text"
          rows={10}
          value={bodyText}
          onChange={(event) => {
            setBodyText(event.target.value);
          }}
        />
        <div>
          <Button
            type="button"
            loading={create.isPending}
            onClick={() => {
              setError(null);
              create.mutate();
            }}
          >
            Save draft
          </Button>
        </div>
      </Card>
    </div>
  );
}
